import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppAuth } from '../hooks/useAppAuth';
import {
  CheckCircle, XCircle, Clock, Package, Truck, MapPin,
  AlertTriangle, Brain, ChevronDown, ChevronUp,
} from 'lucide-react';
import { createApiClient } from '../services/api';
import { useRefresh } from '../hooks/useRefresh';
import { RefreshButton } from '../components/RefreshButton';
import { useJobHub } from '../hooks/useSignalR';

interface PendingJob {
  id: string; referenceNumber: string; title: string; description: string;
  jobType: number; isSameDay: boolean; isRelayJob: boolean;
  pickupAddress: string; deliveryAddress: string; distanceMiles: number;
  goodsDescription: string; requiredVehicleType: number;
  customerName: string; createdAt: string;
}

interface AiReview {
  confidenceScore: number;
  recommendation: 'ApproveSuggested' | 'ReviewRequired' | 'FlagForManual';
  summary: string;
  flaggedIssues: string[];
}

const JOB_TYPES     = ['Same-Day', 'Scheduled', 'Relay', 'Repair', 'Removal', 'Freight'];
const VEHICLE_TYPES = ['Car', 'Motorbike', 'Van', 'LGV', 'HGV'];

const scoreColor = (score: number) =>
  score >= 80 ? 'text-green-700 bg-green-50 border-green-200'
  : score >= 50 ? 'text-amber-700 bg-amber-50 border-amber-200'
  : 'text-red-700 bg-red-50 border-red-200';

export function AdminReviewPage() {
  const { getToken, account } = useAppAuth();
  const qc = useQueryClient();

  const client = createApiClient(getToken);

  const [notes,    setNotes]    = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [aiScores, setAiScores] = useState<Record<string, AiReview>>({});
  const [aiLoading, setAiLoading] = useState<Record<string, boolean>>({});

  const { data: jobs = [], isLoading, refetch } = useQuery<PendingJob[]>({
    queryKey: ['jobs', 'pending'],
    queryFn: () => client.get<PendingJob[]>('/api/jobs/pending').then(r => r.data),
    enabled: !!account,
    refetchInterval: 30_000,
  });

  const { isRefreshing, lastRefreshed, refresh } = useRefresh(refetch);

  // New jobs entering the pending queue are broadcast as NewJobAvailable
  useJobHub({ getToken, enabled: !!account, onNewJobAvailable: () => refetch() });

  const approveMut = useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      client.post(`/api/jobs/${id}/approve`, { notes: note ?? null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs', 'pending'] }),
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      client.post(`/api/jobs/${id}/reject`, { notes: note }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs', 'pending'] }),
  });

  const loadAiReview = async (jobId: string) => {
    if (aiScores[jobId] || aiLoading[jobId]) return;
    setAiLoading(s => ({ ...s, [jobId]: true }));
    try {
      const r = await client.get<AiReview>(`/api/admin/jobs/${jobId}/ai-review`);
      setAiScores(s => ({ ...s, [jobId]: r.data }));
    } catch {
      // ignore — admin can still act without AI score
    } finally {
      setAiLoading(s => ({ ...s, [jobId]: false }));
    }
  };

  const toggle = (id: string) => {
    setExpanded(e => ({ ...e, [id]: !e[id] }));
    loadAiReview(id);
  };

  const sorted = [...jobs].sort((a, b) => Number(b.isSameDay) - Number(a.isSameDay));

  if (isLoading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="page-shell">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Job Review Queue</h1>
          <p className="text-sm text-gray-500 mt-1">
            Only jobs flagged by AI for manual review appear here.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <RefreshButton onRefresh={refresh} isRefreshing={isRefreshing} lastRefreshed={lastRefreshed} />
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600">{jobs.length}</div>
            <div className="text-xs text-gray-500">pending</div>
          </div>
        </div>
      </div>

      {jobs.length > 10 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-2 text-sm text-amber-700">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          High queue volume — prioritise same-day jobs (⚡).
        </div>
      )}

      {jobs.length === 0 ? (
        <div className="text-center py-20">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Queue is clear.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sorted.map(job => {
            const ageMin = Math.floor((Date.now() - new Date(job.createdAt).getTime()) / 60000);
            const overdue = ageMin > 120;
            const isOpen  = expanded[job.id];
            const ai      = aiScores[job.id];
            const busy    = approveMut.isPending || rejectMut.isPending;

            return (
              <div key={job.id} className={`card p-5 ${job.isSameDay ? 'border-amber-300 bg-amber-50/30' : ''}`}>
                {/* Header row */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-gray-400">{job.referenceNumber}</span>
                      {job.isSameDay  && <span className="badge-amber">⚡ Same-Day</span>}
                      {job.isRelayJob && <span className="badge-purple">🔄 Relay</span>}
                      <span className="badge-blue">{JOB_TYPES[job.jobType] ?? 'Unknown'}</span>
                      <span className="badge-gray">
                        <Truck className="inline w-3 h-3 mr-0.5" />
                        {VEHICLE_TYPES[job.requiredVehicleType] ?? '?'}
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-900">{job.title}</h3>
                    <p className="text-sm text-gray-500">Posted by {job.customerName}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className={`text-right text-xs ${overdue ? 'text-red-600' : 'text-gray-400'}`}>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {ageMin < 60 ? `${ageMin}m ago` : `${Math.floor(ageMin / 60)}h ${ageMin % 60}m ago`}
                      </div>
                      {overdue && <div className="font-medium">SLA breach</div>}
                    </div>
                    <button onClick={() => toggle(job.id)}
                      className="text-gray-400 hover:text-gray-600 transition-colors">
                      {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Expanded detail */}
                {isOpen && (
                  <>
                    <div className="grid md:grid-cols-2 gap-3 mb-3 text-sm">
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-400 font-medium">PICKUP</p>
                            <p className="text-gray-700">{job.pickupAddress}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-400 font-medium">DELIVERY</p>
                            <p className="text-gray-700">{job.deliveryAddress}</p>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-start gap-2">
                          <Package className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-400 font-medium">GOODS</p>
                            <p className="text-gray-700">{job.goodsDescription}</p>
                          </div>
                        </div>
                        {job.description && (
                          <p className="text-xs text-gray-500 ml-6">{job.description}</p>
                        )}
                        <p className="text-xs text-gray-400 ml-6">{job.distanceMiles.toFixed(1)} miles</p>
                      </div>
                    </div>

                    {/* AI review panel */}
                    <div className="mb-3 rounded-lg border p-3 bg-gray-50">
                      <div className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
                        <Brain className="w-4 h-4 text-purple-500" />
                        AI Review
                        {aiLoading[job.id] && (
                          <span className="animate-spin w-3 h-3 border-2 border-purple-400 border-t-transparent rounded-full ml-1" />
                        )}
                      </div>
                      {ai ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded border ${scoreColor(ai.confidenceScore)}`}>
                              Score {ai.confidenceScore}/100
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded border ${
                              ai.recommendation === 'FlagForManual'
                                ? 'text-red-700 bg-red-50 border-red-200'
                                : ai.recommendation === 'ReviewRequired'
                                ? 'text-amber-700 bg-amber-50 border-amber-200'
                                : 'text-green-700 bg-green-50 border-green-200'
                            }`}>
                              {ai.recommendation}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600">{ai.summary}</p>
                          {ai.flaggedIssues.length > 0 && (
                            <ul className="text-xs text-red-600 space-y-0.5 list-disc list-inside">
                              {ai.flaggedIssues.map((issue, i) => <li key={i}>{issue}</li>)}
                            </ul>
                          )}
                        </div>
                      ) : !aiLoading[job.id] ? (
                        <p className="text-xs text-gray-400">AI score unavailable.</p>
                      ) : null}
                    </div>

                    {/* Action row */}
                    <div className="flex items-start gap-3">
                      <textarea
                        value={notes[job.id] ?? ''}
                        onChange={e => setNotes(n => ({ ...n, [job.id]: e.target.value }))}
                        rows={2}
                        placeholder="Notes (required for rejection)..."
                        className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2
                                   focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none placeholder-gray-400"
                      />
                      <div className="flex flex-col gap-2 shrink-0">
                        <button
                          onClick={() => approveMut.mutate({ id: job.id, note: notes[job.id] })}
                          disabled={busy}
                          className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700
                                     disabled:opacity-50 text-white text-sm font-medium px-4 py-2
                                     rounded-lg transition-colors">
                          <CheckCircle className="w-4 h-4" /> Approve
                        </button>
                        <button
                          onClick={() => {
                            if (!notes[job.id]?.trim()) { alert('Enter a rejection reason first.'); return; }
                            rejectMut.mutate({ id: job.id, note: notes[job.id] });
                          }}
                          disabled={busy}
                          className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700
                                     disabled:opacity-50 text-white text-sm font-medium px-4 py-2
                                     rounded-lg transition-colors">
                          <XCircle className="w-4 h-4" /> Reject
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {/* Collapsed summary for quick scanning */}
                {!isOpen && (
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {job.pickupAddress.split(',')[0]}
                      <span className="mx-1">→</span>
                      {job.deliveryAddress.split(',')[0]}
                    </span>
                    <span>{job.distanceMiles.toFixed(1)} mi</span>
                    <span className="ml-auto text-blue-600 underline cursor-pointer"
                      onClick={() => toggle(job.id)}>
                      Review
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
